import type { SvgIconComponent } from '@mui/icons-material';
import AdUnitsOutlined from '@mui/icons-material/AdUnitsOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import TextFieldsOutlined from '@mui/icons-material/TextFieldsOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import TrackChangesOutlined from '@mui/icons-material/TrackChangesOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined';
import type { FilterFamilyId } from './filterFamilies';

export type FilterKey =
  | 'format'
  | 'adType'
  | 'os'
  | 'platformBreakdown'
  | 'country'
  | 'placement'
  | 'adStatus'
  | 'campaignStatus'
  | 'campaignObjective'
  | 'optimizationGoal'
  | 'concept'
  | 'duration'
  | 'feature'
  | 'tag'
  | 'creativeId'
  | 'campaignName'
  | 'adSet'
  | 'adName';

/** A tenant-declared dimension, keyed by its SDK token: the server's catalog names them per scope. */
export type CustomFilterKey = `custom:${string}`;
export type AnyFilterKey = FilterKey | CustomFilterKey;

export interface FilterDefinition {
  key: AnyFilterKey;
  label: string;
  family: FilterFamilyId;
  icon: SvgIconComponent;
  /** Options the app owns; every other filter's values come from the server, routed by its catalog. */
  staticOptions?: readonly string[];
  dependsOn?: FilterKey;
}

export const DURATION_BUCKETS = ['0-9s', '10-19s', '20-29s', '30-39s', '40-49s', '50-59s', '60s+'];

const CUSTOM_FILTER_KEY_PATTERN = /^custom:[A-Za-z][A-Za-z0-9_]*$/;

export const FILTER_CATALOG: FilterDefinition[] = [
  {
    key: 'format',
    label: 'Format',
    family: 'creative',
    icon: GridViewOutlined,
  },
  {
    key: 'adType',
    label: 'Ad Type',
    family: 'campaign',
    icon: LocalOfferOutlined,
  },
  {
    key: 'os',
    label: 'OS',
    family: 'targeting',
    icon: ViewColumnOutlined,
  },
  {
    key: 'platformBreakdown',
    label: 'Platform Breakdown',
    family: 'breakdown',
    icon: DevicesOutlined,
  },
  {
    key: 'country',
    label: 'Country',
    family: 'breakdown',
    icon: ExploreOutlined,
  },
  {
    key: 'placement',
    label: 'Placement',
    family: 'breakdown',
    icon: AdUnitsOutlined,
  },
  {
    key: 'adStatus',
    label: 'Ad Status',
    family: 'campaign',
    icon: CheckCircleOutlined,
  },
  {
    key: 'campaignStatus',
    label: 'Campaign Status',
    family: 'campaign',
    icon: ToggleOnOutlined,
  },
  {
    key: 'campaignObjective',
    label: 'Campaign Objective',
    family: 'campaign',
    icon: TrackChangesOutlined,
  },
  {
    key: 'optimizationGoal',
    label: 'Optimization Goal',
    family: 'campaign',
    icon: TuneOutlined,
  },
  {
    key: 'concept',
    label: 'Concept',
    family: 'creative',
    icon: LightbulbOutlined,
  },
  {
    key: 'duration',
    label: 'Duration',
    family: 'creative',
    icon: ScheduleOutlined,
    staticOptions: DURATION_BUCKETS,
  },
  {
    key: 'feature',
    label: 'Element',
    family: 'creative',
    icon: ExtensionOutlined,
  },
  {
    key: 'tag',
    label: 'Tag',
    family: 'creative',
    icon: SellOutlined,
    dependsOn: 'feature',
  },
  {
    key: 'creativeId',
    label: 'Creative ID',
    family: 'creative',
    icon: FingerprintOutlined,
  },
  {
    key: 'campaignName',
    label: 'Campaign Name',
    family: 'campaign',
    icon: DescriptionOutlined,
  },
  {
    key: 'adSet',
    label: 'Ad Set',
    family: 'campaign',
    icon: FolderOutlined,
  },
  {
    key: 'adName',
    label: 'Ad Name',
    family: 'campaign',
    icon: TextFieldsOutlined,
  },
];

type CatalogFilterKey = (typeof FILTER_CATALOG)[number]['key'];
type MissingCatalogKeys = Exclude<FilterKey, CatalogFilterKey>;
const allFilterKeysCovered: MissingCatalogKeys extends never ? true : MissingCatalogKeys = true;
void allFilterKeysCovered;

export const FILTER_INDEX: Record<FilterKey, FilterDefinition> = Object.fromEntries(
  FILTER_CATALOG.map((definition) => [definition.key, definition]),
) as Record<FilterKey, FilterDefinition>;

export function isCustomFilterKey(key: string): key is CustomFilterKey {
  return CUSTOM_FILTER_KEY_PATTERN.test(key);
}

/** How a filter presents. A tenant-declared one has no entry here, so it takes the label the
 * server's catalog gave it and the campaign family its values describe. */
export function filterDefinition(key: AnyFilterKey, label?: string): FilterDefinition {
  if (!isCustomFilterKey(key)) return FILTER_INDEX[key];
  return { key, label: label ?? key, family: 'campaign', icon: FilterAltOutlined };
}
